import { useState } from 'react';
import { Loader2, CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useRsvpStatus } from '@/hooks/useRsvpStatus';
import {
  appsAdvApiRouterRsvpToRide,
  appsAdvApiRouterUnrsvpFromRide,
  appsAdvApiRouterJoinWaitlist,
  appsAdvApiRouterLeaveWaitlist,
  RideOut,
} from '@/lib/api';
import { RiderCount } from './RiderCount';
import { OrganizerRsvpSection } from './OrganizerRsvpSection';

interface RsvpSectionProps {
  ride: RideOut;
}

export const RsvpSection = ({ ride }: RsvpSectionProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const {
    is_rsvped,
    is_waitlisted,
    waitlist_position,
    loading: isLoading,
    error,
    refresh,
  } = useRsvpStatus(ride.id);
  const [actionLoading, setActionLoading] = useState(false);

  const isOrganizer = user?.id === ride.created_by?.id;
  const isGuest = !user;
  const rsvpCount = ride.rsvp_count ?? 0;
  const isFull =
    ride.max_participants != null && rsvpCount >= ride.max_participants;

  const handleRsvp = async () => {
    try {
      setActionLoading(true);
      const response = await appsAdvApiRouterRsvpToRide(ride.id);
      if (response.data.message) {
        toast({
          title: 'RSVP Submitted',
          description: response.data.message,
        });
      } else {
        toast({
          title: 'RSVP Confirmed',
          description: "You're on the list for this ride!",
        });
      }
      refresh();
    } catch (error: any) {
      toast({
        title: 'RSVP Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnRsvp = async () => {
    try {
      setActionLoading(true);
      await appsAdvApiRouterUnrsvpFromRide(ride.id);
      toast({
        title: 'RSVP Cancelled',
        description: 'You have been removed from this ride.',
      });
      refresh();
    } catch (error: any) {
      toast({
        title: 'Failed to Cancel RSVP',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleJoinWaitlist = async () => {
    try {
      setActionLoading(true);
      await appsAdvApiRouterJoinWaitlist(ride.id);
      toast({
        title: 'Waitlist Joined',
        description: "You'll be notified when a spot opens up.",
      });
      refresh();
    } catch (error: any) {
      toast({
        title: 'Failed to Join Waitlist',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleLeaveWaitlist = async () => {
    try {
      setActionLoading(true);
      await appsAdvApiRouterLeaveWaitlist(ride.id);
      toast({
        title: 'Left Waitlist',
        description: 'You have been removed from the waitlist.',
      });
      refresh();
    } catch (error: any) {
      toast({
        title: 'Failed to Leave Waitlist',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="glass-card rounded-lg p-6">
        <div className="flex items-center gap-2">
          <Loader2 className="animate-spin text-adv-orange" />
          <span className="text-adv-lightGray">Loading...</span>
        </div>
      </div>
    );
  }

  // Error state: hide count, show button only
  if (error) {
    return (
      <div className="glass-card rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">Join This Ride</h2>
        <Button
          className="btn-primary w-full"
          onClick={handleRsvp}
          disabled={actionLoading || isGuest}
        >
          {actionLoading ? (
            <>
              <Loader2 size={18} className="animate-spin mr-2" />
              Processing...
            </>
          ) : (
            "I'll Be There!"
          )}
        </Button>
      </div>
    );
  }

  // Organizer: show tabbed interface
  if (isOrganizer) {
    return <OrganizerRsvpSection ride={ride} />;
  }

  // Guest: disabled button with tooltip
  if (isGuest) {
    return (
      <div className="glass-card rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">Join This Ride</h2>
        <RiderCount
          count={rsvpCount}
          maxParticipants={ride.max_participants ?? undefined}
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button disabled className="w-full mt-4">
              I'll Be There!
            </Button>
          </TooltipTrigger>
          <TooltipContent>Log in to join this ride</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  // User RSVP'd
  if (is_rsvped) {
    return (
      <div className="glass-card rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">Join This Ride</h2>
        <RiderCount
          count={rsvpCount}
          maxParticipants={ride.max_participants ?? undefined}
        />
        <div className="mt-4 flex items-center gap-2 text-green-500">
          <CheckCircle size={20} />
          <span className="font-medium">You're RSVP'd!</span>
        </div>
        <Button
          variant="outline"
          className="w-full mt-4"
          onClick={handleUnRsvp}
          disabled={actionLoading}
        >
          {actionLoading ? (
            <>
              <Loader2 size={18} className="animate-spin mr-2" />
              Cancelling...
            </>
          ) : (
            'Cancel RSVP'
          )}
        </Button>
      </div>
    );
  }

  // User on waitlist
  if (is_waitlisted) {
    return (
      <div className="glass-card rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">Join This Ride</h2>
        <RiderCount
          count={rsvpCount}
          maxParticipants={ride.max_participants ?? undefined}
        />
        <div className="mt-4 text-adv-orange">
          You're #{waitlist_position} on the waitlist
        </div>
        <Button
          variant="outline"
          className="w-full mt-4"
          onClick={handleLeaveWaitlist}
          disabled={actionLoading}
        >
          {actionLoading ? (
            <>
              <Loader2 size={18} className="animate-spin mr-2" />
              Leaving...
            </>
          ) : (
            'Leave Waitlist'
          )}
        </Button>
      </div>
    );
  }

  // Ride is full: show join waitlist
  if (isFull) {
    return (
      <div className="glass-card rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">Join This Ride</h2>
        <RiderCount
          count={rsvpCount}
          maxParticipants={ride.max_participants ?? undefined}
        />
        <Button
          variant="outline"
          className="w-full mt-4"
          onClick={handleJoinWaitlist}
          disabled={actionLoading}
        >
          {actionLoading ? (
            <>
              <Loader2 size={18} className="animate-spin mr-2" />
              Joining...
            </>
          ) : (
            'Join Waitlist'
          )}
        </Button>
      </div>
    );
  }

  // Default: show RSVP button
  return (
    <div className="glass-card rounded-lg p-6">
      <h2 className="text-xl font-bold mb-4">Join This Ride</h2>
      <RiderCount
        count={rsvpCount}
        maxParticipants={ride.max_participants ?? undefined}
      />
      <Button
        className="btn-primary w-full mt-4"
        onClick={handleRsvp}
        disabled={actionLoading}
      >
        {actionLoading ? (
          <>
            <Loader2 size={18} className="animate-spin mr-2" />
            Confirming...
          </>
        ) : (
          "I'll Be There!"
        )}
      </Button>
      <p className="text-xs text-adv-lightGray mt-2 text-center">
        By clicking, you confirm you've read the ride details
      </p>
    </div>
  );
};
